// FAQList.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import LinkIcon from '@mui/icons-material/Link';
import ClearIcon from '@mui/icons-material/Clear';

type FAQItem = {
  id: string;
  question: string;
  answer: string;
  tag: string;
};

const TOKENS = {
  canvas: '#F4F7FA',
  surface: '#FFFFFF',
  surfaceSoft: '#F8FAFC',
  line: '#DCE4EC',
  lineSoft: '#E9EEF3',
  ink: '#111827',
  text: '#344054',
  sub: '#667085',
  muted: '#98A2B3',
  blue: '#2D6CDF',
  blueSoft: '#EEF4FF',
  green: '#177A52',
  greenSoft: '#EDF8F3',
  amber: '#A46712',
  amberSoft: '#FFF8EA',
  red: '#C43D4F',
  redSoft: '#FFF0F2',
};

const faqData: FAQItem[] = [
  {
    id: 'what-is-policyintel',
    question: 'PolicyIntel은 어떤 서비스인가요?',
    answer:
      'PolicyIntel은 시장·업종·종목·수급·정책·이슈·공시·재무 데이터를 한 화면에 나열하는 서비스가 아니라, 현재 무엇을 먼저 확인해야 하는지 판단 흐름으로 압축하는 시장 인텔리전스 서비스입니다.\n\n핵심 흐름은 “판단 → 변화 → 근거 → 무효화 → 검증”입니다. 현재 상태만 보여주는 데서 끝나지 않고, 무엇이 달라졌는지와 그 판단을 뒷받침하거나 약화시키는 근거를 함께 확인하는 것을 목표로 합니다.',
    tag: '서비스',
  },
  {
    id: 'guest-access',
    question: '로그인하지 않아도 무엇을 볼 수 있나요?',
    answer:
      '비로그인 상태에서는 오늘 시장 신호와 공개 가능한 종목 후보, 스캐너 미리보기, 정책·이슈·공시 등 공개 정보를 확인할 수 있습니다.\n\n다만 종목 상세의 심화 데이터, 개인 보유종목 추적, 변화 이력, 개인화 알림과 같은 기능은 로그인 후 제공됩니다. 비로그인 화면의 목적은 가입을 먼저 요구하는 것이 아니라 PolicyIntel이 어떤 방식으로 시장을 좁혀 보는지 먼저 확인하게 하는 것입니다.',
    tag: '로그인',
  },
  {
    id: 'authenticated-home',
    question: '로그인하면 메인페이지에서 무엇이 달라지나요?',
    answer:
      '로그인 후 메인페이지는 시장 전체 설명보다 사용자에게 중요한 변화를 우선합니다.\n\n대표적으로 다음을 먼저 확인합니다.\n\n· 어제 이후 내 종목·업종·이슈에서 달라진 것\n· 현재 보유종목의 위험 또는 회복 변화\n· 지금 우선 확인할 시장·업종·종목\n· 새 공시와 정책·이슈 연결\n· 추적 중인 종목의 가격·수급·판단 변화\n\n변화가 없는 정보는 작게 유지하고, 실제로 상태가 달라진 항목을 더 눈에 띄게 보여주는 방향으로 구성됩니다.',
    tag: '로그인',
  },
  {
    id: 'main-page-flow',
    question: '메인페이지는 어떤 순서로 보면 되나요?',
    answer:
      '처음부터 모든 숫자를 읽을 필요는 없습니다.\n\n1) 오늘 시장에서 먼저 볼 위험과 기회를 확인합니다.\n2) 로그인 상태라면 “어제 이후 달라진 것”을 먼저 봅니다.\n3) 강하거나 약한 업종과 수급 흐름을 확인합니다.\n4) 스크리너나 메인 후보에서 종목을 좁힙니다.\n5) 종목 인텔리전스에서 판단·근거·변화·차트를 확인합니다.\n6) 정책·이슈·공시·재무가 현재 판단을 지지하는지 다시 검증합니다.\n\n메인페이지는 최종 답을 대신 내리는 화면이 아니라 다음 확인 대상을 빠르게 압축하는 브리핑 화면입니다.',
    tag: '사용법',
  },
  {
    id: 'changes-since-yesterday',
    question: '“어제 이후 달라진 것”은 무엇인가요?',
    answer:
      '사용자가 추적 중인 대상의 현재 상태를 이전 상태와 비교해 의미 있는 변화만 모아 보여주는 기능입니다.\n\n예를 들어 수급 강화·약화, 위험 조건 추가 또는 해소, 새 공시, 판단 상태 변경 등이 포함될 수 있습니다. 단순 가격 등락만으로 모든 항목을 변화로 만들지 않고, 실제 판단에 영향을 주는 변화가 있는지를 우선합니다.',
    tag: '변화',
  },
  {
    id: 'screener-purpose',
    question: '종목 스크리너는 무엇을 위한 화면인가요?',
    answer:
      '스크리너는 종목을 추천하는 화면이 아니라 조건에 맞는 후보를 빠르게 찾는 FIND 도구입니다.\n\n전략 프리셋을 먼저 선택하고 필요한 조건을 추가한 뒤 결과 테이블에서 후보를 비교합니다. 결과를 펼치면 가격·수급·재무·밸류·변동성·위험 등 세부 근거를 확인할 수 있습니다.\n\n스크리너 점수와 조건 충족 표시는 후보를 압축하기 위한 기준이며 미래 수익률이나 매수 성공 확률을 의미하지 않습니다.',
    tag: '스크리너',
  },
  {
    id: 'stock-workbench',
    question: '종목 인텔리전스에서는 무엇을 확인하나요?',
    answer:
      '종목 화면은 검색 → 후보 → 현재가 → 판단/차트/근거/변화의 흐름으로 이어지는 하나의 Workbench입니다.\n\n현재 판단, 가격 위치, 외국인·기관 수급, 거래대금, 재무·사업 정보, 정책·이슈·공시를 서로 분리된 정보가 아니라 같은 종목 판단을 검증하는 근거로 연결해서 봅니다.',
    tag: '종목',
  },
  {
    id: 'judgment-meaning',
    question: '“판단”은 매수·매도 신호인가요?',
    answer:
      '아닙니다. PolicyIntel의 판단은 현재 조건을 우선검토·관찰·주의 등으로 분류해 “지금 무엇을 확인해야 하는가”를 보여주는 상태값입니다.\n\n판단이 좋아 보인다고 해서 미래 상승을 보장하지 않으며, 주의 상태라고 해서 반드시 하락한다는 뜻도 아닙니다. 가격·수급·시장 위험·재무·이슈 등 현재 확보된 근거가 어떤 방향으로 정렬되어 있는지를 요약한 것입니다.',
    tag: '판단',
  },
  {
    id: 'evidence-strength',
    question: '근거 강도나 evidence strength는 상승 확률인가요?',
    answer:
      '아닙니다. 근거 강도는 필요한 데이터가 얼마나 확보되어 있고, 여러 근거가 현재 판단과 얼마나 일관되게 정렬되어 있는지를 나타내는 보조 지표입니다.\n\n예를 들어 evidence strength 80%는 “80% 확률로 오른다”는 의미가 아닙니다. 데이터 확보도와 근거 정렬도가 상대적으로 높다는 뜻으로 해석해야 합니다.',
    tag: '판단',
  },
  {
    id: 'decision-chart',
    question: 'Decision Chart는 일반 주가 차트와 무엇이 다른가요?',
    answer:
      'Decision Chart의 목적은 매수·매도 타이밍을 예측하는 것이 아니라 판단이 언제, 왜 바뀌었는지 추적하는 것입니다.\n\n가격과 거래량, 선택한 지표를 보면서 판단 상태가 실제로 변경된 시점을 함께 확인합니다. 반복적인 작은 신호를 계속 찍기보다 의미 있는 판단 변화가 있었던 지점을 중심으로 보는 것이 기본 원칙입니다.',
    tag: '차트',
  },
  {
    id: 'chart-events',
    question: '차트의 정책·이슈 이벤트는 가격 원인을 뜻하나요?',
    answer:
      '아닙니다. 어떤 이벤트가 같은 시기에 존재했다는 사실과 그 이벤트가 가격 변동의 직접 원인이라는 주장은 구분해야 합니다.\n\n이벤트 표시는 기본적으로 최소화하고, 직접 연관성이 확인된 이벤트를 사용자가 필요할 때 확인하는 방식입니다. 이벤트가 많다는 이유만으로 Judgment 점수를 자동으로 올리거나 내리지 않습니다.',
    tag: '차트',
  },
  {
    id: 'change-tab',
    question: '종목의 “변화” 탭에서는 무엇을 보나요?',
    answer:
      '현재 판단 상태 자체보다 “이전과 비교해 무엇이 바뀌었는가”를 확인하는 탭입니다.\n\n수급 방향 변화, 가격 기준 변화, 위험 조건 추가·해소, 새 공시나 직접 연결 이벤트, 판단 전환 등이 주요 대상입니다. 변화가 없는 항목은 반복해서 강조하지 않는 것이 원칙입니다.',
    tag: '변화',
  },
  {
    id: 'supply-flow',
    question: '외국인·기관·프로그램 수급은 어떻게 사용하나요?',
    answer:
      '수급은 단독 매수 신호가 아니라 가격·시장 환경·업종 흐름과 함께 보는 핵심 근거입니다.\n\n화면에서는 외국인·기관·프로그램 흐름을 서로 구분해 표시하며, 값이 실제 0인 경우와 아직 수집되지 않았거나 기준시각이 다른 경우를 같은 상태로 처리하지 않습니다. 숫자뿐 아니라 기준시각과 데이터 상태를 함께 확인해야 합니다.',
    tag: '수급',
  },
  {
    id: 'issue-report',
    question: '정책·이슈 리포트는 어떻게 읽으면 되나요?',
    answer:
      '이슈 리포트는 기사 목록을 많이 보여주는 화면보다 영향도와 종목 연결 근거를 먼저 확인하는 Research Timeline에 가깝습니다.\n\n이슈가 언제 발생했는지, 국내 종목과 직접 연결되는지 또는 추가 검토가 필요한지, 현재 판단에 반영할 근거가 있는지를 순서대로 확인합니다. 이슈의 존재 자체보다 실제 사업·산업 연결 근거가 중요합니다.',
    tag: '이슈',
  },
  {
    id: 'issue-direct-review-watch',
    question: '이슈의 직접 영향·검토 후보·관찰 후보는 어떻게 다른가요?',
    answer:
      '직접 영향은 원문 또는 사업 연결 근거에서 특정 기업과의 직접 연관성이 확인된 경우입니다.\n\n검토 후보는 기업 또는 사업 영역의 연결 가능성은 확인됐지만 수주·매출 영향까지 확정할 수 없는 경우입니다.\n\n관찰 후보는 직접 연결은 아니지만 산업·공급망·섹터 노출 가능성이 있어 후속 확인이 필요한 경우입니다.\n\n이 분류는 종목 추천 등급이 아니라 연결 근거의 수준을 구분하기 위한 것입니다.',
    tag: '이슈',
  },
  {
    id: 'disclosure-intelligence',
    question: '실시간 공시는 단순 DART 목록과 무엇이 다른가요?',
    answer:
      '공시를 시간순으로만 나열하지 않고 영향도·리스크·우선순위 기준으로 먼저 확인할 이벤트를 압축합니다.\n\n최근 공시를 검색·필터링할 수 있고, 상세에서는 공시 요약, 판단 경로, 관련 종목, 영향도와 원문 근거를 함께 확인합니다. 공시가 있다는 이유만으로 자동으로 호재·악재를 단정하지 않습니다.',
    tag: '공시',
  },
  {
    id: 'business-financial',
    question: '사업·재무 정보는 현재 판단에 어떻게 사용하나요?',
    answer:
      '단기 가격과 수급만으로 종목을 보지 않기 위해 사업 구조와 재무 상태를 함께 확인합니다.\n\n사업 개요, 산업 분류, 주요 제품·키워드, 재무 품질, 수익성, 부채, 현금흐름, 밸류에이션과 같은 정보를 판단 근거로 사용합니다. 일부 지표가 좋다고 해서 자동으로 우량 또는 매수 판단으로 연결하지 않고 다른 위험 근거와 함께 봅니다.',
    tag: '재무',
  },
  {
    id: 'holdings',
    question: '보유종목을 등록하면 무엇이 달라지나요?',
    answer:
      '보유종목은 단순 관심 목록이 아니라 개인화 변화 추적의 기준이 됩니다.\n\n등록한 종목의 현재 판단, 가격·수급 변화, 위험 조건, 관련 정책·이슈·공시를 우선 확인할 수 있고, 상태가 달라진 종목은 메인페이지의 변화 영역과 보유종목 화면에서 더 먼저 보여줍니다.',
    tag: '보유종목',
  },
  {
    id: 'alerts',
    question: '알림은 어떤 경우에 사용하나요?',
    answer:
      '알림은 단순 가격 상승·하락을 계속 보내는 기능보다 사용자가 추적 중인 판단과 위험 조건의 변화를 놓치지 않게 하는 데 목적이 있습니다.\n\n알림을 받았을 때는 해당 종목이나 변화 위치로 이동해 어떤 조건이 달라졌는지와 근거를 다시 확인하는 방식으로 사용하는 것이 좋습니다.',
    tag: '알림',
  },
  {
    id: 'market-session',
    question: '장중·장전·야간·주말에는 같은 데이터를 보여주나요?',
    answer:
      '같은 화면이라도 시장 상태에 따라 우선해서 봐야 할 데이터가 달라집니다.\n\n장중에는 현재 가격·거래대금·수급과 실시간 위험을 중심으로 보고, 장 마감 후에는 당일 확정 흐름과 다음 거래일 준비 신호를 봅니다. 야간에는 해외 시장과 선행 데이터의 변화를 참고하고, 주말에는 마지막 거래일 이후 새로 들어온 이슈·공시·보유종목 변화를 중심으로 확인하는 방향입니다.',
    tag: '시장',
  },
  {
    id: 'data-state',
    question: '값이 비어 있거나 “확인 중”으로 표시되는 이유는 무엇인가요?',
    answer:
      '모든 데이터가 같은 시각과 주기로 제공되는 것은 아닙니다. 시장이 아직 열리지 않았거나, 공식 데이터가 갱신되지 않았거나, 수집 지연·기준일 불일치가 있을 수 있습니다.\n\nPolicyIntel은 값이 없을 때 과거 값을 오늘 값처럼 채워 넣기보다 미수집·지연·기준일 불일치 등의 상태를 구분해 표시하는 것을 우선합니다.',
    tag: '데이터',
  },
  {
    id: 'recommendation',
    question: 'PolicyIntel은 종목 추천이나 자동 매수·매도 서비스인가요?',
    answer:
      '아닙니다. PolicyIntel은 현재 시장 상태와 판단 근거를 정리해 사용자가 직접 검토할 수 있도록 돕는 서비스입니다.\n\nJudgment, 스크리너 점수, 이슈 연결, 수급 강도 등 어떤 값도 미래 수익을 보장하거나 주문을 대신하는 의미가 아닙니다. 최종 투자 판단과 주문 책임은 사용자에게 있으며 시장 급변, 데이터 지연, 모델 오판 가능성을 함께 고려해야 합니다.',
    tag: '투자원칙',
  },
  {
    id: 'best-practice',
    question: '실전에서는 어떤 순서로 활용하는 것이 좋나요?',
    answer:
      '한 숫자나 한 화면만 보고 결론을 내리기보다 다음 순서로 확인하는 것이 좋습니다.\n\n1) 메인에서 오늘 우선 확인할 시장 변화 확인\n2) 스크리너에서 조건에 맞는 후보 압축\n3) 종목 판단에서 현재 상태 확인\n4) Decision Chart에서 판단이 바뀐 시점 확인\n5) 수급·가격·재무 근거 확인\n6) 정책·이슈·공시에서 새로운 위험 또는 직접 연결 확인\n7) 보유·관심종목으로 등록한 뒤 이후 변화 추적\n\n여러 근거가 같은 방향을 가리키는지는 중요한 검증 요소지만, 그 자체가 확정 수익을 의미하지는 않습니다.',
    tag: '활용법',
  },
  {
    id: 'beta-and-accuracy',
    question: 'BETA 표시와 데이터 검증은 무엇을 의미하나요?',
    answer:
      '일부 판단·연결·실시간 수집 기능은 실제 운영 데이터에서 계속 검증하고 개선하는 단계입니다.\n\n화면의 결과는 원천 데이터, 기준시각, 판단 변화 이력과 함께 확인할 수 있도록 설계하고 있으며, 충분한 운영 이력이나 검증이 없는 기능을 확정적 신호처럼 보여주지 않는 것을 원칙으로 합니다.',
    tag: '데이터',
  },
];

const highlight = (text: string, q: string) => {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return (
    <>
      {before}
      <mark
        style={{
          background: TOKENS.blueSoft,
          padding: '0 2px',
          color: TOKENS.ink,
          borderRadius: 2,
        }}
      >
        {match}
      </mark>
      {after}
    </>
  );
};

const useHash = () => {
  const [hash, setHash] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '',
  );

  useEffect(() => {
    const onHash = () => setHash(window.location.hash.replace('#', ''));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return hash;
};

const FAQList: React.FC = () => {
  const [query, setQuery] = useState('');
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [expandedAll, setExpandedAll] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const hashId = useHash();

  useEffect(() => {
    if (!hashId) return;
    setExpandedSet(new Set([hashId]));
    const el = document.getElementById(hashId);
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hashId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqData;
    return faqData.filter(
      (faq) =>
        faq.question.toLowerCase().includes(q) ||
        faq.answer.toLowerCase().includes(q) ||
        faq.tag.toLowerCase().includes(q),
    );
  }, [query]);

  useEffect(() => {
    const allOpen = filtered.length > 0 && filtered.every((faq) => expandedSet.has(faq.id));
    setExpandedAll(allOpen);
  }, [filtered, expandedSet]);

  const toggleAll = useCallback(() => {
    if (expandedAll) setExpandedSet(new Set());
    else setExpandedSet(new Set(filtered.map((faq) => faq.id)));
    setExpandedAll(!expandedAll);
  }, [expandedAll, filtered]);

  const handleToggle = (id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: TOKENS.canvas, py: { xs: 2.5, md: 4.5 } }}>
      <Container maxWidth="lg">
        <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
          <Box sx={{ mb: { xs: 2.2, md: 3 } }}>
            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.7 }}>
              <Typography sx={{ color: TOKENS.blue, fontSize: 11, fontWeight: 900, letterSpacing: '.12em' }}>
                POLICYINTEL GUIDE
              </Typography>
              <Divider flexItem orientation="vertical" sx={{ borderColor: TOKENS.line }} />
              <Typography sx={{ color: TOKENS.muted, fontSize: 11, fontWeight: 800 }}>
                FAQ
              </Typography>
            </Stack>

            <Typography
              component="h1"
              sx={{
                color: TOKENS.ink,
                fontSize: { xs: 28, sm: 34, md: 40 },
                lineHeight: 1.15,
                letterSpacing: '-.04em',
                fontWeight: 950,
              }}
            >
              PolicyIntel 사용 가이드
            </Typography>
            <Typography sx={{ mt: 0.8, color: TOKENS.sub, fontSize: { xs: 13, md: 14 }, lineHeight: 1.7 }}>
              판단 → 변화 → 근거 → 무효화 → 검증의 흐름으로 시장과 종목을 확인하는 방법을 정리했습니다.
            </Typography>
          </Box>

          <Box
            sx={{
              borderTop: `1px solid ${TOKENS.line}`,
              borderBottom: `1px solid ${TOKENS.line}`,
              bgcolor: TOKENS.surface,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
            }}
          >
            {[
              ['오늘', '시장 브리핑'],
              ['탐색', '스크리너'],
              ['종목', '판단·차트·근거'],
              ['보유', '개인화 변화'],
              ['알림', '변화 재확인'],
            ].map(([label, value], index) => (
              <Box
                key={label}
                sx={{
                  px: 1.5,
                  py: 1.15,
                  minWidth: 0,
                  borderRight: { md: index < 4 ? `1px solid ${TOKENS.lineSoft}` : 'none' },
                  borderBottom: { xs: index < 4 ? `1px solid ${TOKENS.lineSoft}` : 'none', md: 'none' },
                }}
              >
                <Typography sx={{ color: TOKENS.muted, fontSize: 10, fontWeight: 850 }}>{label}</Typography>
                <Typography sx={{ mt: 0.2, color: TOKENS.text, fontSize: 12, fontWeight: 900 }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mt: 2.2, mb: 1.4 }}
          >
            <TextField
              fullWidth
              placeholder="판단, 스크리너, 공시, 보유종목 등으로 검색"
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: TOKENS.muted }} />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <Tooltip title="검색어 지우기">
                      <IconButton size="small" onClick={() => setQuery('')} sx={{ color: TOKENS.muted }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 44,
                  borderRadius: 1.5,
                  bgcolor: TOKENS.surface,
                  color: TOKENS.ink,
                  '& fieldset': { borderColor: TOKENS.line },
                  '&:hover fieldset': { borderColor: '#B9C6D4' },
                  '&.Mui-focused fieldset': { borderColor: TOKENS.blue, borderWidth: 1 },
                },
                '& .MuiInputBase-input': { WebkitTextFillColor: TOKENS.ink, fontSize: 13 },
              }}
            />

            <Tooltip title={expandedAll ? '전체 접기' : '전체 펼치기'}>
              <IconButton
                aria-label={expandedAll ? 'Collapse all' : 'Expand all'}
                onClick={toggleAll}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  border: `1px solid ${TOKENS.line}`,
                  bgcolor: TOKENS.surface,
                  color: TOKENS.sub,
                  '&:hover': { bgcolor: TOKENS.blueSoft, color: TOKENS.blue, borderColor: '#C8D8F4' },
                }}
              >
                {expandedAll ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ borderTop: `1px solid ${TOKENS.line}`, bgcolor: TOKENS.surface }}>
            {filtered.map((faq) => {
              const isExpanded = expandedSet.has(faq.id);
              return (
                <Accordion
                  key={faq.id}
                  id={faq.id}
                  expanded={isExpanded}
                  onChange={() => handleToggle(faq.id)}
                  disableGutters
                  elevation={0}
                  square
                  sx={{
                    m: 0,
                    bgcolor: TOKENS.surface,
                    borderBottom: `1px solid ${TOKENS.lineSoft}`,
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { m: 0 },
                    '&:hover': { bgcolor: '#FBFCFE' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: TOKENS.sub }} />}
                    sx={{
                      px: { xs: 1.2, sm: 1.6 },
                      minHeight: 60,
                      '&.Mui-expanded': { minHeight: 60 },
                      '& .MuiAccordionSummary-content': { my: 1.05 },
                      '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.05 },
                    }}
                  >
                    <Stack direction="row" gap={1} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                      <Chip
                        label={faq.tag}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 23,
                          flex: '0 0 auto',
                          borderColor: '#D6DEE8',
                          bgcolor: TOKENS.surfaceSoft,
                          color: TOKENS.sub,
                          fontSize: 10,
                          fontWeight: 850,
                        }}
                      />
                      <Typography sx={{ flex: 1, minWidth: 0, color: TOKENS.ink, fontSize: { xs: 13.2, sm: 14 }, fontWeight: 900, lineHeight: 1.45 }}>
                        {highlight(faq.question, query)}
                      </Typography>
                      <Tooltip title={copiedId === faq.id ? '복사됨' : '이 질문 링크 복사'}>
                        <IconButton
                          size="small"
                          sx={{ color: TOKENS.muted }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const url = `${window.location.origin}${window.location.pathname}#${faq.id}`;
                            void navigator.clipboard.writeText(url);
                            setCopiedId(faq.id);
                            window.setTimeout(() => setCopiedId(null), 1200);
                          }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: { xs: 1.25, sm: 1.6 }, pt: 0, pb: 1.7, bgcolor: '#FBFCFE' }}>
                    <Typography sx={{ maxWidth: 940, whiteSpace: 'pre-line', color: TOKENS.sub, fontSize: 13, lineHeight: 1.78 }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {filtered.length === 0 && (
              <Box sx={{ py: 7, px: 2, textAlign: 'center', borderBottom: `1px solid ${TOKENS.line}` }}>
                <Typography sx={{ color: TOKENS.text, fontSize: 13, fontWeight: 900 }}>검색 결과가 없습니다.</Typography>
                <Typography sx={{ mt: 0.35, color: TOKENS.muted, fontSize: 12 }}>다른 키워드로 다시 검색해 주세요.</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 1.5, px: 0.2 }}>
            <Typography sx={{ color: TOKENS.muted, fontSize: 10.5, lineHeight: 1.6 }}>
              PolicyIntel의 판단·점수·이슈 연결은 투자 수익을 보장하는 신호가 아닙니다. 데이터 기준시각과 근거를 함께 확인해 주세요.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FAQList;
